import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { Loading } from '../components/Loading';
import { userService } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { User, CreateUserData } from '../types';
import { showToast } from '../utils/toast';

export const Users: React.FC = () => {
  const { user: authUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CreateUserData>({
    username: '',
    email: '',
    password: '',
    role: authUser?.role === 'admin' ? 'client' : 'moderator',
  });
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = authUser?.role === 'admin';
  const isModerator = authUser?.role === 'moderator';
  const isClient = authUser?.role === 'client';

  useEffect(() => {
    // Only load users if we have a token
    const token = localStorage.getItem('access_token');
    if (token) {
      loadUsers();
    } else {
      setLoading(false);
      showToast.error('Not authenticated. Please log in again.');
    }
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Check token before making request
      const token = localStorage.getItem('access_token');
      if (!token) {
        showToast.error('Authentication token missing. Please log in again.');
        setLoading(false);
        return;
      }

      // Load users based on role
      let response;
      if (isAdmin) {
        response = await userService.getUsers();
      } else {
        // Clients and moderators load their moderators
        response = await userService.getModerators();
      }

      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        showToast.error(response.message || 'Failed to load users');
      }
    } catch (err: any) {
      // Better error handling
      if (err.response?.status === 401 || err.response?.status === 422) {
        const errorMsg = err.response?.data?.message || '';
        if (errorMsg.toLowerCase().includes('token') ||
          errorMsg.toLowerCase().includes('authorization') ||
          errorMsg.toLowerCase().includes('authentication')) {
          showToast.error('Authentication failed. Please log in again.');
        } else {
          showToast.error(err.message || 'Failed to load users');
        }
      } else {
        showToast.error(err.message || 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Ensure role is set correctly for moderators and clients
      const submitData = {
        ...formData,
        role: authUser?.role === 'admin' ? formData.role : 'moderator'
      };
      const response = await userService.createUser(submitData);
      if (response.success) {
        showToast.success('User created successfully!');
        setFormData({
          username: '',
          email: '',
          password: '',
          role: authUser?.role === 'admin' ? 'client' : 'moderator'
        });
        setShowForm(false);
        loadUsers();
      } else {
        showToast.error(response.message || 'Failed to create user');
      }
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const response = await userService.deleteUser(userId);
      if (response.success) {
        showToast.success('User deleted successfully!');
        loadUsers();
      } else {
        showToast.error(response.message || 'Failed to delete user');
      }
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to delete user');
    }
  };

  if (loading) {
    return <Loading fullScreen text="Loading users..." />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isAdmin ? 'User Management' : 'Moderator Management'}
        </h1>
        {isClient && (
          <Button
            onClick={() => setShowForm(!showForm)}
            variant={showForm ? 'secondary' : 'primary'}
          >
            {showForm ? 'Cancel' : '+ Add Moderator'}
          </Button>
        )}
        {isAdmin && (
          <Button
            onClick={() => setShowForm(!showForm)}
            variant={showForm ? 'secondary' : 'primary'}
          >
            {showForm ? 'Cancel' : '+ Add New User'}
          </Button>
        )}
      </div>


      {showForm && (
        <Card title={isAdmin ? "Register New User" : "Create Moderator"} className="mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Username"
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              placeholder="Enter username"
            />
            <Input
              label="Email (Optional)"
              type="email"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter email"
            />
            <Input
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              placeholder="Enter password (min 6 characters)"
              helperText="Password must be at least 6 characters"
            />
            {isAdmin && (
              <Select
                label="Role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'client' | 'moderator' })}
              >
                <option value="client">Client</option>
                <option value="moderator">Moderator</option>
                <option value="admin">Admin only can access</option>
              </Select>
            )}
            {isClient && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <input
                  type="text"
                  value="Moderator"
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Moderators will have access to your dashboard and data
                </p>
              </div>
            )}
            <Button type="submit" variant="primary" isLoading={submitting} className="w-full">
              {isAdmin ? 'Create User' : 'Create Moderator'}
            </Button>
          </form>
        </Card>
      )}

      <Card title={isAdmin ? "All Users" : "My Moderators"}>
        {users.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            {isAdmin
              ? 'No users found. Create your first user above.'
              : isClient
                ? 'No moderators found. Create your first moderator above.'
                : 'No moderators found.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : user.role === 'moderator'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-blue-100 text-blue-800'
                          }`}
                      >
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {/* Only admins and clients can delete (moderators cannot delete) */}
                      {(isAdmin || isClient) && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(user.id)}
                        >
                          Delete
                        </Button>
                      )}
                      {isModerator && (
                        <span className="text-sm text-gray-400 italic">No delete permission</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

